import { useState } from 'react';
import { apiBaseInfo, setApiOverride, normalizeApiBase } from './config';
import { IconX, IconCheckCircle, IconAlertTriangle, IconPlug } from './Icons';

// On-device editor for the API host the packaged app talks to.
//
// Exists because the Android test build hard-codes this laptop's Wi-Fi address
// at build time, and that address changes with every network. Without this the
// only fix was a rebuild + re-install from the laptop, and the symptom was a
// silent one: a storefront that loads its shell and then shows nothing at all.
//
// Reachable by long-pressing the header logo, and only inside the Capacitor
// shell -- see canOverrideApiBase() in config.js for why the web must not
// honour an override at all.
export default function DevApiSettings({ onClose }) {
  const info = apiBaseInfo();
  const [value, setValue] = useState(info.override || info.builtIn || '');
  const [probe, setProbe] = useState(null); // { ok, detail }
  const [busy, setBusy] = useState(false);

  const invalid = normalizeApiBase(value) === null;

  // Checks the address before it is committed, so a typo surfaces here rather
  // than as an empty storefront after the reload.
  const test = async () => {
    const target = normalizeApiBase(value);
    if (target === null) return;
    setBusy(true);
    setProbe(null);
    try {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), 6000);
      const res = await fetch(`${target}/api/status`, { signal: controller.signal });
      clearTimeout(timer);
      setProbe(res.ok
        ? { ok: true, detail: `Reachable (HTTP ${res.status})` }
        : { ok: false, detail: `Answered with HTTP ${res.status}` });
    } catch (err) {
      setProbe({
        ok: false,
        detail: err?.name === 'AbortError'
          ? 'No response within 6s -- wrong address, or the laptop firewall is blocking it.'
          : 'Could not connect. Check both devices are on the same Wi-Fi.',
      });
    }
    setBusy(false);
  };

  const save = () => {
    if (!setApiOverride(value)) return;
    // Full reload: API_BASE_URL is read once at module load and captured by
    // every module that imports it, so nothing short of a fresh boot makes
    // the whole app agree on the new host.
    window.location.reload();
  };

  const reset = () => {
    if (!setApiOverride('')) return;
    window.location.reload();
  };

  return (
    <div className="devapi-backdrop" role="dialog" aria-modal="true" aria-label="API connection">
      <div className="devapi-card">
        <div className="devapi-head">
          <span className="devapi-title"><IconPlug size={16} /> API Connection</span>
          <button type="button" className="devapi-close" onClick={onClose} aria-label="Close">
            <IconX size={16} />
          </button>
        </div>

        <p className="devapi-note">
          The address this app loads products from. Change it when the laptop
          running the server moves to a different network.
        </p>

        <dl className="devapi-facts">
          <div><dt>In use now</dt><dd>{info.active || '(same origin)'}</dd></div>
          <div><dt>Built into this APK</dt><dd>{info.builtIn || '(same origin)'}</dd></div>
        </dl>

        <label className="devapi-label" htmlFor="devapi-url">Server address</label>
        <input
          id="devapi-url"
          className="devapi-input"
          value={value}
          onChange={(e) => { setValue(e.target.value); setProbe(null); }}
          placeholder="http://192.168.1.10:4000"
          inputMode="url"
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
        />
        {invalid && (
          <p className="devapi-msg devapi-bad">
            <IconAlertTriangle size={13} /> Use scheme, host and port only — no trailing path.
          </p>
        )}
        {probe && (
          <p className={`devapi-msg ${probe.ok ? 'devapi-good' : 'devapi-bad'}`}>
            {probe.ok ? <IconCheckCircle size={13} /> : <IconAlertTriangle size={13} />} {probe.detail}
          </p>
        )}

        <div className="devapi-actions">
          <button type="button" className="devapi-btn devapi-btn-test" onClick={test} disabled={invalid || busy}>
            {busy ? 'Testing…' : 'Test'}
          </button>
          <button type="button" className="devapi-btn devapi-btn-save" onClick={save} disabled={invalid}>
            Save &amp; Reload
          </button>
        </div>
        <button type="button" className="devapi-reset" onClick={reset}>
          Reset to the built-in address
        </button>
      </div>
    </div>
  );
}
